import { useCallback, useContext, useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import type { ContentfulArticleSearchResponse } from 'autogen/swagger/all';
import { SectionSelectButton, TopicSelectButton } from 'autogen/translation-keys/trans-website-articleuniverse-filter';
import ArticleSearchResponseContext from 'components/pages/ArticleUniverse/components/Filter/context/ArticleSearchResponseContext';
import { breakpoint } from 'variables';
import type { ToggleOptionToFilterArrayType } from '../../shared/types';
import { mapItemToFilterOptions } from './helpers/mappers';
import SelectorButton from './SelectorButton';

const ButtonContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${Spacing16};

    ${breakpoint.up('1025px')} {
        display: flex;
    }
`;

type FilterProps = {
    sections: ContentfulArticleSearchResponse['sections'];
    topics: ContentfulArticleSearchResponse['topics'];
};

function Filter({ topics, sections }: FilterProps) {
    const { t } = useTranslation();
    const articleSearchResponseContext = useContext(ArticleSearchResponseContext);
    const topicOptions = useMemo(() => mapItemToFilterOptions(topics), [topics]);
    const sectionOptions = useMemo(() => mapItemToFilterOptions(sections), [sections]);

    const toggleOptionToFilterArray = useCallback(
        ({ queryParameterNames, selectType }: ToggleOptionToFilterArrayType) => {
            if (selectType === 'topic') {
                articleSearchResponseContext?.updateArticlePage({ topic: queryParameterNames });
            }
            if (selectType === 'section') {
                articleSearchResponseContext?.updateArticlePage({ section: queryParameterNames });
            }
        },
        [articleSearchResponseContext]
    );

    return (
        <ButtonContainer>
            <SelectorButton
                buttonLabel={t(TopicSelectButton)}
                selectType="topic"
                selectOptions={topicOptions}
                toggleOptionToFilterArray={toggleOptionToFilterArray}
            />
            <SelectorButton
                buttonLabel={t(SectionSelectButton)}
                selectType="section"
                selectOptions={sectionOptions}
                toggleOptionToFilterArray={toggleOptionToFilterArray}
            />
        </ButtonContainer>
    );
}

export default withErrorBoundary(Filter, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
